// Hot tub layouts: the ONE description of each hot tub's plan and section, read
// by both the 2D renderer and the 3D preview so the two can never disagree about
// where a seat is or how deep the water sits. Pure and DOM-free (unit-tested).
//
// Each model is measured off the manufacturer's overhead view (the `…_OH` image
// on its jacuzzi.com page) in its own reference frame — mm, centred, +y = the top
// edge of that image — and takes its size from the same page. A layout is built
// in that frame and scaled to the instance's own width / depth / height, so the
// proportions hold if an entry is ever given other dimensions.
//
// Which model an instance is comes from `hotTubModel` (on the template, copied to
// the instance at placement) — NOT the template id, which a Duplicate replaces.
// An unknown or absent model draws as the Virtus, the first one.

import type { Vec2 } from '../shared/types';
import { polygonBoolean } from './polybool';

/** Per-corner radii of a rectangle; an absent corner is square. */
export interface CornerRadii { tl?: number; tr?: number; br?: number; bl?: number }

/** A smooth inward bulge in one straight edge — a moulded scoop or the housing
 *  of a control panel. `at` is its centre along the edge (mm, in the frame's x
 *  for a top/bottom edge, y for a side), `half` its half-width, `depth` how far
 *  it reaches into the shape. A cos² profile, so it meets the edge tangentially. */
export interface Bump { edge: 'top' | 'bottom' | 'left' | 'right'; at: number; half: number; depth: number }

/**
 * A rectangle with independently rounded corners and optional inward bumps,
 * wound counter-clockwise from the bottom-left, arcs tessellated. A radius is
 * capped at half the shorter side, and consecutive coincident points are
 * dropped: `THREE.Shape` triangulation breaks on a repeated vertex, and this
 * ring is fed straight into it.
 */
export function roundedRect(x0: number, y0: number, x1: number, y1: number, r: CornerRadii = {}, bumps: Bump[] = []): Vec2[] {
  const cap = Math.min(x1 - x0, y1 - y0) / 2;
  const R = (v?: number): number => Math.max(0, Math.min(v ?? 0, cap));
  const bl = R(r.bl), br = R(r.br), tr = R(r.tr), tl = R(r.tl);
  const pts: Vec2[] = [];
  const corner = (cx: number, cy: number, rad: number, a0: number): void => {
    if (rad <= 0) { pts.push({ x: cx, y: cy }); return; }
    const N = 12;
    for (let i = 0; i <= N; i++) {
      const a = a0 + (Math.PI / 2) * (i / N);
      pts.push({ x: cx + rad * Math.cos(a), y: cy + rad * Math.sin(a) });
    }
  };
  const lift = (edge: Bump['edge'], s: number): number => bumps.reduce((acc, b) => {
    if (b.edge !== edge || b.half <= 0) return acc;
    const t = (s - b.at) / b.half;
    return Math.abs(t) >= 1 ? acc : acc + b.depth * Math.cos((Math.PI / 2) * t) ** 2;
  }, 0);
  // The straight run between two corners, sampled only when a bump sits on it.
  const edge = (e: Bump['edge'], from: number, to: number, at: (s: number, d: number) => Vec2): void => {
    if (!bumps.some((b) => b.edge === e)) return;
    const N = 48;
    for (let i = 1; i < N; i++) { const s = from + ((to - from) * i) / N; pts.push(at(s, lift(e, s))); }
  };
  corner(x0 + bl, y0 + bl, bl, Math.PI);
  edge('bottom', x0 + bl, x1 - br, (s, d) => ({ x: s, y: y0 + d }));
  corner(x1 - br, y0 + br, br, 1.5 * Math.PI);
  edge('right', y0 + br, y1 - tr, (s, d) => ({ x: x1 - d, y: s }));
  corner(x1 - tr, y1 - tr, tr, 0);
  edge('top', x1 - tr, x0 + tl, (s, d) => ({ x: s, y: y1 - d }));
  corner(x0 + tl, y1 - tl, tl, 0.5 * Math.PI);
  edge('left', y1 - tl, y0 + bl, (s, d) => ({ x: x0 + d, y: s }));
  return clean(pts);
}

/** Drop consecutive coincident points (and a closing repeat). */
function clean(pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (!q || Math.hypot(p.x - q.x, p.y - q.y) > 1e-6) out.push(p);
  }
  if (out.length > 1 && Math.hypot(out[0].x - out[out.length - 1].x, out[0].y - out[out.length - 1].y) <= 1e-6) out.pop();
  return out;
}

const signedArea = (ring: Vec2[]): number => ring.reduce((s, p, i) => {
  const q = ring[(i + 1) % ring.length];
  return s + p.x * q.y - q.x * p.y;
}, 0) / 2;

/** `ring` cut to `bound`: its largest piece, counter-clockwise. A seat is drawn as
 *  a rectangle out to the shell wall, and the wall is not always straight — a
 *  moulded bump would otherwise have a seat poking through it into the coping.
 *  Falls back to the ring itself if the clip comes back empty (a degenerate
 *  case), so a seat can never silently vanish. */
function clipTo(ring: Vec2[], bound: Vec2[]): Vec2[] {
  const pieces = polygonBoolean({ outer: ring, holes: [] }, { outer: bound, holes: [] }, 'intersection');
  let best: Vec2[] | null = null, bestA = 0;
  for (const p of pieces) { const a = Math.abs(signedArea(p.outer)); if (a > bestA) { bestA = a; best = p.outer; } }
  if (!best || best.length < 3) return ring;
  const c = clean(best);
  return signedArea(c) < 0 ? c.reverse() : c;
}

export interface HotTubSeat {
  /** Plan outline, in the instance's local frame. */
  poly: Vec2[];
  /** Height of its surface above the floor (mm). */
  top: number;
}

export interface HotTubLayout {
  /** The coping's finish: Jacuzzi's mosaic tile, or the moulded acrylic rim. */
  coping: 'mosaic' | 'acrylic';
  /** Cabinet / deck outer edge. */
  outline: Vec2[];
  /** Outer edge of the coping. */
  rim: Vec2[];
  /** Inner edge of the coping: the opening the water fills. */
  shell: Vec2[];
  /** The shell, pulled in a hair, for the water body. Its walls must not be
   *  coplanar with the coping's inner wall, or the two z-fight in 3D. */
  water: Vec2[];
  footwell: Vec2[];
  drain: { c: Vec2; r: number };
  /** The upright seats. */
  seats: HotTubSeat[];
  /** The lounge position, if the model has one: ONE outline in plan, and the
   *  reclined steps a flat-topped prism can express (back raised, hips lowest). */
  lounger: { outline: Vec2[]; steps: HotTubSeat[] } | null;
  /** Raised areas that are not seats — an entry step, the moulding beside a
   *  lounger's headrest. Drawn like seats, never counted as one. */
  platforms: HotTubSeat[];
  /** Heights above the floor (mm). */
  z: {
    /** Top of the deck, which is the tub's overall height. */
    deck: number;
    /** Top of the cabinet, where the deck cap starts. */
    cabinet: number;
    rim: number;
    water: number;
    /** The ledge the footwell is sunk into. */
    ledge: number;
    /** The footwell floor, the lowest point of the shell. */
    floor: number;
  };
}

// ---- The model registry (reference frames, mm) ----

interface Rect { x0: number; y0: number; x1: number; y1: number; r?: CornerRadii; bumps?: Bump[] }
interface SeatRect extends Rect { top: number }
interface HotTubModel {
  /** The published size, and the frame everything below is measured in. */
  ref: { w: number; d: number; h: number };
  coping: HotTubLayout['coping'];
  outline: Rect; rim: Rect; shell: Rect; footwell: Rect;
  /** Where the overhead view shows the drain; the footwell centre if absent. */
  drain?: { x: number; y: number; r: number };
  seats: SeatRect[];
  lounger?: { outline: Rect; steps: SeatRect[] };
  platforms?: SeatRect[];
  z: HotTubLayout['z'];
}

const all = (v: number): CornerRadii => ({ tl: v, tr: v, br: v, bl: v });
const F = 60; // the rounded lip of a seat's front edge
// The seats differ in depth on purpose — "different slant and depth" is how
// Jacuzzi describes them — and every one is at least 400 mm under the water.
const Z_930 = { deck: 930, cabinet: 890, rim: 915, water: 810, ledge: 200, floor: 80 };
const Z_900 = { deck: 900, cabinet: 860, rim: 895, water: 790, ledge: 200, floor: 80 };

export const HOT_TUB_MODELS: Record<string, HotTubModel> = {
  // Jacuzzi® Virtus — 250 × 215 × 93 cm, seven seats (Virtus_OH.png). Two
  // diagonally opposite corners rounded, the other two square and carrying the
  // control panel and the filter; a mosaic coping; a three-seat bench along one
  // long side, a seat on each short side, a corner seat, the lounger along the
  // other long side, and the footwell.
  virtus: {
    ref: { w: 2500, d: 2150, h: 930 },
    coping: 'mosaic',
    outline: { x0: -1250, y0: -1075, x1: 1250, y1: 1075, r: { tr: 400, bl: 400 } },
    rim: { x0: -1166, y0: -1008, x1: 1166, y1: 1008, r: all(280) },
    shell: { x0: -972, y0: -833, x1: 972, y1: 833, r: all(190) },
    footwell: { x0: -335, y0: -224, x1: 499, y1: 314, r: all(70) },
    seats: [
      { x0: -972, y0: 420, x1: -300, y1: 833, r: { tl: 190, br: F }, top: 360 },
      { x0: -300, y0: 420, x1: 400, y1: 833, r: { bl: F, br: F }, top: 330 },
      { x0: 400, y0: 420, x1: 972, y1: 833, r: { tr: 190, bl: F }, top: 380 },
      { x0: -972, y0: -237, x1: -520, y1: 420, r: { tr: F, br: F }, top: 340 },
      { x0: 560, y0: -237, x1: 972, y1: 420, r: { tl: F, bl: F }, top: 350 },
      { x0: 560, y0: -833, x1: 972, y1: -237, r: { br: 190, tl: F }, top: 390 },
    ],
    lounger: {
      outline: { x0: -972, y0: -833, x1: 560, y1: -237, r: { bl: 190, tr: F } },
      steps: [
        { x0: -972, y0: -833, x1: -560, y1: -237, r: { bl: 190 }, top: 500 }, // back and head
        { x0: -560, y0: -833, x1: -100, y1: -237, top: 230 },                 // hips
        { x0: -100, y0: -833, x1: 560, y1: -237, r: { tr: F }, top: 290 },    // legs
      ],
    },
    z: Z_930,
  },

  // Jacuzzi® Profile — 239 × 215 × 93 cm (Profile_OH.png). All four corners
  // rounded, a mosaic coping whose bottom edge waves up into the tub, the lounger
  // down the whole left side (head at the top wall), four seats round the
  // footwell and an entry step below it: five places.
  profile: {
    ref: { w: 2390, d: 2150, h: 930 },
    coping: 'mosaic',
    outline: { x0: -1195, y0: -1075, x1: 1195, y1: 1075, r: all(230) },
    rim: { x0: -1132, y0: -1005, x1: 1132, y1: 1005, r: all(300), bumps: [{ edge: 'bottom', at: 13, half: 470, depth: 75 }] },
    shell: { x0: -945, y0: -834, x1: 945, y1: 844, r: all(190), bumps: [{ edge: 'bottom', at: 13, half: 470, depth: 94 }] },
    footwell: { x0: -260, y0: -319, x1: 455, y1: 332, r: all(100) },
    drain: { x: 19, y: -191, r: 40 },
    seats: [
      { x0: -255, y0: 332, x1: 295, y1: 844, r: { bl: F, br: F }, top: 350 },
      { x0: 295, y0: 260, x1: 945, y1: 844, r: { tr: 190, bl: F }, top: 380 },
      { x0: 484, y0: -342, x1: 945, y1: 260, r: { tl: F, bl: F }, top: 350 },
      { x0: 484, y0: -834, x1: 945, y1: -342, r: { br: 190, tl: F }, top: 390 },
    ],
    lounger: {
      outline: { x0: -945, y0: -834, x1: -255, y1: 844, r: { tl: 190, bl: 190 } },
      steps: [
        { x0: -945, y0: 260, x1: -255, y1: 844, r: { tl: 190 }, top: 500 },   // back and head
        { x0: -945, y0: 20, x1: -255, y1: 260, top: 230 },                    // hips
        { x0: -945, y0: -834, x1: -255, y1: 20, r: { bl: 190 }, top: 290 },  // legs
      ],
    },
    platforms: [{ x0: -255, y0: -834, x1: 484, y1: -319, top: 480 }],     // entry step
    z: Z_930,
  },

  // Jacuzzi® Lodge+ S — 185 × 165 × 90 cm, "seating for 2 to 3 adults"
  // (Lodge_S_OH.jpg). A moulded acrylic rim with a headrest scoop at the top and
  // the control-panel housing at the bottom; the footwell up top, a seat either
  // side of it, and the lounger across the bottom with its pillow on the right.
  'lodge-s': {
    ref: { w: 1850, d: 1650, h: 900 },
    coping: 'acrylic',
    outline: { x0: -925, y0: -825, x1: 925, y1: 825, r: all(170) },
    rim: { x0: -905, y0: -805, x1: 905, y1: 805, r: all(150) },
    shell: {
      x0: -880, y0: -800, x1: 880, y1: 726, r: all(175),
      bumps: [{ edge: 'top', at: 5, half: 490, depth: 120 }, { edge: 'bottom', at: 265, half: 200, depth: 134 }],
    },
    footwell: { x0: -304, y0: -121, x1: 293, y1: 528, r: all(70) },
    drain: { x: 0, y: 3, r: 35 },
    seats: [
      { x0: -880, y0: -8, x1: -416, y1: 726, r: { br: F }, top: 370 },
      { x0: 426, y0: -51, x1: 880, y1: 726, r: { bl: F }, top: 360 },
    ],
    lounger: {
      outline: { x0: -880, y0: -800, x1: 637, y1: -136, r: { tr: F } },
      steps: [
        { x0: -880, y0: -800, x1: -250, y1: -136, top: 290 },                 // legs
        { x0: -250, y0: -800, x1: 250, y1: -136, top: 230 },                  // hips
        { x0: 250, y0: -800, x1: 637, y1: -136, r: { tr: F }, top: 500 },     // back and head
      ],
    },
    platforms: [{ x0: 637, y0: -800, x1: 880, y1: -51, top: 520 }],       // beside the pillow
    z: Z_900,
  },

  // Jacuzzi® Lodge+ M — 200 × 185 × 90 cm, "seating for 4 to 5 people"
  // (Lodge+M_OH.png). No lounger: three seats along the top, one either side of
  // the footwell, and a skimmer ledge below it where the rim bulges in.
  'lodge-m': {
    ref: { w: 2000, d: 1850, h: 900 },
    coping: 'acrylic',
    outline: { x0: -1000, y0: -925, x1: 1000, y1: 925, r: all(200) },
    rim: { x0: -980, y0: -905, x1: 980, y1: 905, r: all(180) },
    shell: { x0: -913, y0: -807, x1: 913, y1: 839, r: all(190), bumps: [{ edge: 'bottom', at: 102, half: 480, depth: 146 }] },
    footwell: { x0: -300, y0: -504, x1: 278, y1: 329, r: all(110) },
    drain: { x: 73, y: 216, r: 35 },
    seats: [
      { x0: -913, y0: 378, x1: -250, y1: 839, r: { tl: 190, br: F }, top: 380 },
      { x0: -250, y0: 362, x1: 300, y1: 839, r: { bl: F, br: F }, top: 350 },
      { x0: 300, y0: 346, x1: 913, y1: 839, r: { tr: 190, bl: F }, top: 380 },
      { x0: -913, y0: -807, x1: -310, y1: 378, r: { bl: 190, tr: F, br: F }, top: 360 },
      { x0: 290, y0: -807, x1: 913, y1: 346, r: { br: 190, tl: F, bl: F }, top: 370 },
    ],
    platforms: [{ x0: -310, y0: -807, x1: 290, y1: -504, top: 480 }],     // skimmer ledge
    z: Z_900,
  },

  // Jacuzzi® Lodge+ L — 211 × 223 × 90 cm, "seating for up to 5-6 people"
  // (Lodge_L_OH.jpg). Deeper than it is wide; two headrest scoops in the top rim
  // and the panel housing in the bottom one; three seats along the top, one on
  // each side, and the lounger across the bottom with its pillow on the right.
  'lodge-l': {
    ref: { w: 2110, d: 2230, h: 900 },
    coping: 'acrylic',
    outline: { x0: -1055, y0: -1115, x1: 1055, y1: 1115, r: all(200) },
    rim: { x0: -1035, y0: -1095, x1: 1035, y1: 1095, r: all(180) },
    shell: {
      x0: -975, y0: -1044, x1: 975, y1: 1008, r: all(190),
      bumps: [
        { edge: 'top', at: -262, half: 165, depth: 66 },
        { edge: 'top', at: 267, half: 165, depth: 66 },
        { edge: 'bottom', at: 145, half: 340, depth: 82 },
      ],
    },
    footwell: { x0: -359, y0: -330, x1: 486, y1: 491, r: all(100) },
    seats: [
      { x0: -975, y0: 557, x1: -359, y1: 1008, r: { tl: 190, br: F }, top: 380 },
      { x0: -359, y0: 491, x1: 486, y1: 1008, r: { bl: F, br: F }, top: 350 },
      { x0: 486, y0: 557, x1: 975, y1: 1008, r: { tr: 190, bl: F }, top: 380 },
      { x0: -975, y0: -297, x1: -359, y1: 557, r: { tr: F, br: F }, top: 360 },
      { x0: 486, y0: -330, x1: 975, y1: 557, r: { tl: F, bl: F }, top: 370 },
    ],
    lounger: {
      outline: { x0: -975, y0: -1044, x1: 730, y1: -379, r: { bl: 190, tr: F } },
      steps: [
        { x0: -975, y0: -1044, x1: -300, y1: -379, r: { bl: 190 }, top: 290 }, // legs
        { x0: -300, y0: -1044, x1: 250, y1: -379, top: 230 },                  // hips
        { x0: 250, y0: -1044, x1: 730, y1: -379, r: { tr: F }, top: 500 },     // back and head
      ],
    },
    platforms: [{ x0: 730, y0: -1044, x1: 975, y1: -330, r: { br: 190 }, top: 520 }], // beside the pillow
    z: Z_900,
  },
};

/** The model an instance draws as: its own, else the Virtus. */
export function hotTubModelOf(id: string | undefined): HotTubModel {
  return (id && HOT_TUB_MODELS[id]) || HOT_TUB_MODELS.virtus;
}

/** The Virtus's reference size, kept for callers that predate the registry. */
export const HOT_TUB_REF = HOT_TUB_MODELS.virtus.ref;

/** A hot tub's full layout for an instance of the given size and model. */
export function hotTubLayout(width: number, depth: number, height: number, model?: string): HotTubLayout {
  const m = hotTubModelOf(model);
  const sx = width / m.ref.w, sy = depth / m.ref.d, sz = height / m.ref.h;
  const S = (ring: Vec2[]): Vec2[] => ring.map((p) => ({ x: p.x * sx, y: p.y * sy }));
  const ring = (q: Rect): Vec2[] => roundedRect(q.x0, q.y0, q.x1, q.y1, q.r, q.bumps);
  const shell = ring(m.shell);
  const inShell = (q: SeatRect): HotTubSeat => ({ poly: S(clipTo(ring(q), shell)), top: q.top * sz });
  const sh = m.shell;
  const water = roundedRect(sh.x0 + 3, sh.y0 + 3, sh.x1 - 3, sh.y1 - 3,
    Object.fromEntries(Object.entries(sh.r ?? {}).map(([k, v]) => [k, Math.max(0, (v ?? 0) - 3)])), sh.bumps);
  const fw = m.footwell;
  const drain = m.drain ?? { x: (fw.x0 + fw.x1) / 2, y: (fw.y0 + fw.y1) / 2, r: 40 };

  return {
    coping: m.coping,
    outline: S(ring(m.outline)),
    rim: S(ring(m.rim)),
    shell: S(shell),
    water: S(water),
    footwell: S(ring(fw)),
    drain: { c: { x: drain.x * sx, y: drain.y * sy }, r: drain.r * Math.min(sx, sy) },
    seats: m.seats.map(inShell),
    lounger: m.lounger ? { outline: S(clipTo(ring(m.lounger.outline), shell)), steps: m.lounger.steps.map(inShell) } : null,
    platforms: (m.platforms ?? []).map(inShell),
    z: {
      deck: m.z.deck * sz, cabinet: m.z.cabinet * sz, rim: m.z.rim * sz,
      water: m.z.water * sz, ledge: m.z.ledge * sz, floor: m.z.floor * sz,
    },
  };
}
